import React from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { documentToHtmlString } from "@contentful/rich-text-html-renderer";
import { Helmet } from "react-helmet";

import { contentfulClient } from "config/contentfulClient";
import { US_STATES } from "utils/states";
import { capitalize } from "utils/helpers";

import Col from "../../components/Col";
import PageHeader from "../../components/PageHeader";
import Row from "../../components/Row";
import Container from "../../components/Container";
import Button from "../../components/Button";

import { GET_PAL_JOB_INFORMATION } from "./gql";
import { PalJobCollection } from "types";
import { slugToText } from "utils/helpers";

interface ParamsProps {
  city: string;
  county: string;
  state: string;
}

interface Data {
  palJobCollection: PalJobCollection;
}

const City: React.FC = () => {
  const params = useParams<ParamsProps>();

  const filteredState = US_STATES.find(
    (item) => item.name.replace(/\s+/g, "-").toLowerCase() === params.state
  );

  const { data, loading } = useQuery<Data>(GET_PAL_JOB_INFORMATION, {
    client: contentfulClient,
  });

  if (loading) {
    return null;
  }

  function replaceKeywords(value: string | undefined) {
    if (!value) return "";
    return value
      .replaceAll("%CITY%", slugToText(params.city))
      .replaceAll("%STATE%", filteredState!.abbreviation);
  }

  const {
    metaTitle,
    metaDescription,
    metaImage,
    metaKeywords,
    headerHeading,
    jobDescription,
    jobPostSchema,
    headerImage,
    headerSubheading,
    contentCopy,
    contentHeading,
  } = data?.palJobCollection.items[0] ?? {};

  const OPTIONS = {
    renderNode: {
      "heading-3": (node: any, next: any) =>
        `<h3>${replaceKeywords(next(node.content))}</h3>`,
      paragraph: (node: any, next: any) =>
        `<p>${replaceKeywords(next(node.content))}</p>`,
    },
  };

  return (
    <>
      <Helmet>
        <title>{replaceKeywords(metaTitle)}</title>
        <meta name="title" content={replaceKeywords(metaTitle)}></meta>
        <meta
          name="description"
          content={replaceKeywords(metaDescription)}
        ></meta>
        <meta name="keywords" content={replaceKeywords(metaKeywords)}></meta>

        <meta property="og:type" content="website"></meta>
        <meta
          property="og:url"
          content={`https://www.papa.com/pal-local-jobs/${params.state}/${params.city}`}
        ></meta>
        <meta property="og:title" content={replaceKeywords(metaTitle)}></meta>
        <meta
          property="og:description"
          content={replaceKeywords(metaDescription)}
        ></meta>
        <meta property="og:image" content={`http://${metaImage}`} />

        <meta property="twitter:card" content="summary_large_image"></meta>
        <meta
          property="twitter:url"
          content={`https://www.papa.com/pal-local-jobs/${params.state}/${params.city}`}
        ></meta>
        <meta
          property="twitter:title"
          content={replaceKeywords(metaTitle)}
        ></meta>
        <meta
          property="twitter:description"
          content={replaceKeywords(metaDescription)}
        ></meta>
        <meta property="twitter:image" content={`http://${metaImage}`} />

        <script type="application/ld+json">
          {`{
            "@context" : "https://schema.org/",
            "@type" : "JobPosting",
            "title" : "${replaceKeywords(jobPostSchema?.title)}",
            "description" : "${replaceKeywords(jobPostSchema?.description)}",
            "identifier": {
              "@type": "PropertyValue",
              "name": "${jobPostSchema?.organizationName}",
              "value": "1234567"
            },
            "datePosted" : "${jobPostSchema?.datePosted}",
            "validThrough" : "${jobPostSchema?.validThrough}T00:00",
            "employmentType" : [${jobPostSchema?.employmentType.map(
              (item: any) => `"${item}"`
            )}],
            "hiringOrganization" : {
              "@type" : "Organization",
              "name" : "${jobPostSchema?.organizationName}",
              "sameAs" : "${jobPostSchema?.organizationSameAs}",
              "logo" : "${jobPostSchema?.organizationLogo}"
            },
            "industry": [${jobPostSchema?.industry}],
            "experienceRequirements": "${replaceKeywords(
              jobPostSchema?.experienceRequirements
            )}",
            "educationRequirements": "${replaceKeywords(
              jobPostSchema?.educationRequirements
            )}",
            "qualifications": "${replaceKeywords(
              jobPostSchema?.qualifications
            )}",
            "occupationalCategory": [${jobPostSchema?.occupationalCategory}],
            "responsibilities": "${replaceKeywords(
              jobPostSchema?.responsibilities
            )}",
            "jobBenefits": "${replaceKeywords(jobPostSchema?.jobBenefits)}",
            "skills": "${jobPostSchema?.skills}",
            "jobLocation": {
            "@type": "Place",
              "address": {
              "@type": "PostalAddress",
              "addressLocality": "${slugToText(params.city)}",
              "addressRegion": "${filteredState?.abbreviation}",
              "addressCountry": "US"
              }
            },
            "baseSalary": {
              "@type": "MonetaryAmount",
              "value": {
                "@type": "QuantitativeValue",
                "minValue": ${jobPostSchema?.salaryMinimum},
                "maxValue": ${jobPostSchema?.salaryMaximum},
                "unitText": "${jobPostSchema?.salaryUnitText}"
              }
            }
            "salaryCurrency": "USD"
          }`}
        </script>
      </Helmet>

      <div className="jobs jobs--description">
        <PageHeader>
          <Row>
            <Col md={12}>
              <h1>{replaceKeywords(headerHeading)}</h1>
              <hr />
              <p>{replaceKeywords(headerSubheading)}</p>
            </Col>

            {headerImage && (
              <img
                src={headerImage.url}
                alt="Papa"
                className="page-header__image"
              />
            )}
          </Row>
        </PageHeader>

        <Container className="content">
          <Row>
            <Col md={{ span: 10, offset: 1 }}>
              <div className="overview text-center">
                <h2>{replaceKeywords(contentHeading)}</h2>
                <p>{replaceKeywords(contentCopy)}</p>

                <Button to="/pals/signup">Apply Now</Button>
              </div>
            </Col>
          </Row>

          <div
            className="jobs__description"
            dangerouslySetInnerHTML={{
              __html: documentToHtmlString(
                jobDescription!.json as any,
                OPTIONS
              ),
            }}
          />

          <div className="application-form">
            <Row>
              <Col md={{ span: 6, offset: 3 }} className="text-center">
                <h4 className="mb-20">
                  Papa is growing in {capitalize(filteredState!.name)}. Ready to
                  join us? Get started below.
                </h4>
                <Button to="/pals/signup">Apply Now</Button>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </>
  );
};

export default City;
